// <pfs-system-status>: Container-Card mit Sub-Component pro Service.
//
// Two modes:
//
//  1. Static catalog via data-services (JSON-Array):
//       [{ name, display_name, url, kind }]
//     Sub-Components fetchen ihre eigenen URLs (legacy, für demo-fixed-list).
//
//  2. Dynamic discovery via data-source-url:
//     URL liefert {apps: [{slug, display_name, version, healthy, status, ...}]}
//     entries werden direkt aus der apps[]-Liste gerendert (kein per-service-fetch).
//     Refresh-Button re-fetcht die URL.
//
// Refresh-Button broadcastet Custom-Event 'pfs:refresh' (mode 1) bzw. triggert
// re-fetch der Source-URL (mode 2).

import { defineComponent } from "../../composition/define-component.js";
import { withShadow } from "../../composition/with-shadow.js";
import { apiFetch } from "../../store/api-fetch.js";
import { SHADOW_RESET_CSS } from "../../styles/shadow-reset.js";
import "../card-mini/pfs-card-mini.js";

const REFRESH_EVENT = "pfs:refresh";

// MUSS vor defineComponent declared werden — customElements.define
// upgrade'd Light-DOM-Elemente synchron, dann läuft render() bevor `var`-
// hoisted const-bodies assigned sind. Andernfalls: leerer <style>-Tag.
//
// Die kompakte Card-Shell (Rahmen/Header/Titel/Body + loading/empty) kommt aus
// dem geteilten <pfs-card-mini> (PFS-705) — hier bleibt nur das
// system-status-Spezifische: der Refresh-Button (im actions-Slot), der
// Entries-Container + die Entry-Optik + der Error-Text.
const STYLES = `
${SHADOW_RESET_CSS}
:host { display: block; }
.pfs-system-status__refresh {
  border: 0;
  background: transparent;
  cursor: pointer;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--pfs-color-on-surface-subtle);
  transition: color 150ms ease;
}
.pfs-system-status__refresh:hover {
  color: var(--pfs-color-on-surface-muted);
}
.pfs-system-status__refresh:disabled {
  cursor: wait;
}
.pfs-system-status__refresh.is-loading svg {
  animation: pfs-system-status-spin 1s linear infinite;
}
@keyframes pfs-system-status-spin {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}
.pfs-system-status__entries {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}
.pfs-system-status__error {
  font-size: 0.75rem;
  color: var(--pfs-color-danger);
  padding: 0.25rem 0;
}
.pfs-entry {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  font-size: 0.75rem;
}
.pfs-entry__dot {
  width: 0.375rem;
  height: 0.375rem;
  border-radius: 50%;
  background: var(--pfs-color-on-surface-subtle);
  flex-shrink: 0;
}
.pfs-entry[data-status="ok"] .pfs-entry__dot { background: var(--pfs-color-success); }
.pfs-entry[data-status="error"] .pfs-entry__dot { background: var(--pfs-color-danger); }
.pfs-entry__label-wrap {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
}
.pfs-entry__label {
  color: var(--pfs-color-on-surface-subtle);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.pfs-entry__version {
  color: var(--pfs-color-on-surface-muted);
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, "Cascadia Mono", "Roboto Mono", monospace;
  flex-shrink: 0;
}
`;

interface ServiceCatalogEntry {
  name: string;
  display_name?: string;
  url: string;
  kind?: "internal" | "platform" | string;
}

interface AppEntry {
  slug: string;
  display_name?: string;
  version?: string;
  healthy?: boolean;
  status?: string;
  health_url?: string;
  bff_url?: string;
  probe?: { healthy?: boolean; status?: number; latency_ms?: number };
}

interface SystemStatusResponse {
  apps?: AppEntry[];
}

defineComponent({
  tagName: "pfs-system-status",
  mixins: [withShadow()],
  render(host, ctx) {
    if (!ctx.shadowRoot) return;

    const sourceUrl = host.getAttribute("data-source-url");
    const services = parseServices(host.getAttribute("data-services"));

    // Geteilte kompakte Card-Shell (PFS-705): Header „System" + actions-Slot +
    // Body mit loading/empty/ready. Entries + Refresh sind Light-DOM-Kinder →
    // in den actions- bzw. default-Slot projiziert, weiter von DIESEM Shadow
    // gestylt.
    const card = document.createElement("pfs-card-mini");
    card.setAttribute("data-title", "System");
    card.setAttribute("data-loading-label", "Wird geladen");
    card.setAttribute("data-empty-title", "Keine Apps registriert");

    // mednet-app-style refresh-icon: stroke-width 2, no border-button-chrome,
    // subtle gray that darkens on hover. Spins via .is-loading class while
    // the fetch is in-flight (set in fetchApps below).
    const refreshBtn = document.createElement("button");
    refreshBtn.type = "button";
    refreshBtn.slot = "actions";
    refreshBtn.classList.add("pfs-system-status__refresh");
    refreshBtn.setAttribute("aria-label", "Aktualisieren");
    refreshBtn.title = "Aktualisieren";
    refreshBtn.innerHTML = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182M2.985 19.644l3.181-3.182"/></svg>`;

    const list = document.createElement("div");
    list.classList.add("pfs-system-status__entries");

    const style = document.createElement("style");
    style.textContent = STYLES;

    card.append(refreshBtn, list);
    ctx.shadowRoot.append(style, card);

    const setState = (s: "loading" | "empty" | "ready") => card.setAttribute("data-state", s);

    if (sourceUrl) {
      // Mode 2: dynamic discovery via apps[]-shape
      const renderApps = (apps: AppEntry[]) => {
        if (apps.length === 0) {
          list.replaceChildren();
          setState("empty");
          return;
        }
        // Sort: healthy first, then unhealthy. Within each group: slug
        // alphabetically. Stable sort makes UI predictable across refreshes.
        const sorted = [...apps].sort((a, b) => {
          const ha = a.healthy === true ? 0 : 1;
          const hb = b.healthy === true ? 0 : 1;
          if (ha !== hb) return ha - hb;
          return a.slug.localeCompare(b.slug);
        });
        list.replaceChildren(...sorted.map(renderEntry));
        setState("ready");
      };

      let inFlight = false;
      let hasRendered = false;
      const fetchApps = async () => {
        if (inFlight) return;
        inFlight = true;
        refreshBtn.classList.add("is-loading");
        refreshBtn.disabled = true;
        // Erst-Render: loading-Zustand. Re-Fetch: bestehende Liste sichtbar
        // lassen (Refresh-Spinner reicht) → kein UI-Flicker.
        if (!hasRendered) setState("loading");
        try {
          const data = await apiFetch<SystemStatusResponse>(sourceUrl);
          renderApps(Array.isArray(data.apps) ? data.apps : []);
          hasRendered = true;
        } catch (err) {
          console.warn("[pfs-system-status] fetch failed", err);
          const errEl = document.createElement("div");
          errEl.classList.add("pfs-system-status__error");
          errEl.textContent = "Status nicht verfügbar";
          list.replaceChildren(errEl);
          setState("ready");
        } finally {
          inFlight = false;
          refreshBtn.classList.remove("is-loading");
          refreshBtn.disabled = false;
        }
      };

      refreshBtn.addEventListener("click", () => void fetchApps());
      void fetchApps();
    } else {
      // Mode 1: static catalog via data-services (legacy)
      for (const svc of services) {
        const entry = document.createElement("pfs-system-status-entry");
        entry.setAttribute("data-name", svc.name);
        if (svc.display_name) entry.setAttribute("data-display-name", svc.display_name);
        entry.setAttribute("data-url", svc.url);
        if (svc.kind) entry.setAttribute("data-kind", svc.kind);
        list.appendChild(entry);
      }
      setState("ready");

      refreshBtn.addEventListener("click", () => {
        host.dispatchEvent(new CustomEvent(REFRESH_EVENT, { bubbles: false, composed: true }));
      });
      const onRefresh = () => {
        list.querySelectorAll("pfs-system-status-entry").forEach((el) => {
          el.dispatchEvent(new CustomEvent(REFRESH_EVENT));
        });
      };
      host.addEventListener(REFRESH_EVENT, onRefresh);
      ctx.cleanup.push(() => host.removeEventListener(REFRESH_EVENT, onRefresh));
    }
  },
});

function renderEntry(app: AppEntry): HTMLElement {
  const root = document.createElement("div");
  root.classList.add("pfs-entry");
  const isHealthy = app.healthy === true;
  root.dataset.status = isHealthy ? "ok" : "error";

  // Slug ist sprechender als display_name in Multi-App-Umgebungen
  // (`mednet-main` vs `main-app` ist eindeutig, "Main" allein nicht).
  // display_name wandert ins title-Attribut als Tooltip — Hover-Text mit
  // dem human-readable Namen, falls gesetzt + ungleich slug.
  const labelText = app.slug;
  const tooltip =
    app.display_name && app.display_name !== app.slug
      ? `${app.display_name}${app.version ? " v" + app.version : ""}`
      : app.version
        ? "v" + app.version
        : labelText;

  root.title = tooltip;

  // Layout: { [dot] [label] } ............... [version]
  // mednet-app-Parität: dot+label gehören zusammen (links-flex), version
  // sitzt rechts (justify-between auf root). Label-wrap hat min-width:0
  // damit das ellipsis bei langen slugs greift.
  const labelWrap = document.createElement("div");
  labelWrap.classList.add("pfs-entry__label-wrap");

  const dot = document.createElement("span");
  dot.classList.add("pfs-entry__dot");

  const label = document.createElement("span");
  label.classList.add("pfs-entry__label");
  label.textContent = labelText;

  labelWrap.appendChild(dot);
  labelWrap.appendChild(label);

  const versionEl = document.createElement("span");
  versionEl.classList.add("pfs-entry__version");
  versionEl.textContent = app.version ? "v" + app.version : "—";

  root.appendChild(labelWrap);
  root.appendChild(versionEl);
  return root;
}

function parseServices(raw: string | null): ServiceCatalogEntry[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (err) {
    console.warn("[pfs-system-status] data-services parse error", err);
    return [];
  }
}
